import type { LogLevel, LogEntry } from './types'
import { LogLevelValues } from './types'

/**
 * Browser-compatible log transport that outputs to console with appropriate methods.
 * Supports both pretty-printed (dev) and JSON (prod) output formats.
 *
 * @remarks
 * This transport maps Pino log levels to corresponding console methods:
 * - trace, debug -> console.debug
 * - info -> console.info
 * - warn -> console.warn
 * - error, fatal -> console.error
 */

/** Console method mapping for each log level */
const consoleMethodMap: Record<LogLevel, 'debug' | 'info' | 'warn' | 'error'> = {
  trace: 'debug',
  debug: 'debug',
  info: 'info',
  warn: 'warn',
  error: 'error',
  fatal: 'error',
}

const levelColors: Record<LogLevel, string> = {
  trace: 'color: #808080', // gray
  debug: 'color: #00BFFF', // deep sky blue
  info: 'color: #32CD32', // lime green
  warn: 'color: #FFA500', // orange
  error: 'color: #FF4500', // orange red
  fatal: 'color: #FF0000; font-weight: bold', // red bold
}

/** Level labels for pretty output */
const levelLabels: Record<LogLevel, string> = {
  trace: 'TRACE',
  debug: 'DEBUG',
  info: 'INFO ',
  warn: 'WARN ',
  error: 'ERROR',
  fatal: 'FATAL',
}

const formatTime = (timestamp: number): string => {
  const date = new Date(timestamp)
  return date.toISOString().slice(11, 23) // HH:mm:ss.SSS
}

/**
 * Browser transport configuration.
 */
export interface BrowserTransportOptions {
  /** Enable JSON output (default: false for pretty-printed output) */
  jsonOutput?: boolean
  level?: LogLevel
}

/**
 * Creates a browser-compatible transport function for logging.
 *
 * @param options - Transport configuration options
 * @returns A function that outputs log entries to the browser console
 *
 * @example
 * ```typescript
 * const transport = createBrowserTransport({ jsonOutput: false })
 * transport({
 *   level: 'info',
 *   time: Date.now(),
 *   msg: 'User logged in',
 *   userId: '123'
 * })
 * ```
 */
export const createBrowserTransport = (
  options: BrowserTransportOptions = {}
): ((entry: LogEntry) => void) => {
  const { jsonOutput = false, level: minLevel = 'trace' } = options
  const minLevelValue = LogLevelValues[minLevel]

  return (entry: LogEntry): void => {
    // Filter by minimum level
    const entryLevelValue = LogLevelValues[entry.level]
    if (entryLevelValue < minLevelValue) {
      return
    }

    const consoleMethod = consoleMethodMap[entry.level]

    if (jsonOutput) {
      // Production: Output structured JSON
      // eslint-disable-next-line no-console
      console[consoleMethod](
        JSON.stringify(entry, (_key, value: unknown) =>
          typeof value === 'bigint' ? value.toString() : value
        )
      )
    } else {
      const { level, time, msg, context, ...data } = entry
      const timeStr = formatTime(time)
      const levelLabel = levelLabels[level]
      const contextStr = context ? `[${context}]` : ''

      const prefix = `%c${timeStr} ${levelLabel}${contextStr ? ' ' + contextStr : ''}`
      const style = levelColors[level]

      if (Object.keys(data).length > 0) {
        // eslint-disable-next-line no-console
        console[consoleMethod](prefix, style, msg, data)
      } else {
        // eslint-disable-next-line no-console
        console[consoleMethod](prefix, style, msg)
      }
    }
  }
}
